import type { TFunction } from 'i18next'
import { z } from 'zod'
import { PaytrieAPI } from '@paytrie/client'

import { testCanadianPostalCode } from '@/requests/signup'

const PROVINCE_CODES = Object.values(
  PaytrieAPI.UpdateCurrentUserBodyProvince
) as readonly string[]

export const isValidProvinceCode = (
  value: string
): value is PaytrieAPI.UpdateCurrentUserBodyProvince =>
  PROVINCE_CODES.includes(value)

export const formatPostalCode = (raw: string): string => {
  const cleaned = raw
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 6)
  return cleaned.length > 3
    ? `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`
    : cleaned
}

const trimmed = z.string().trim()

export const addressSchema = z.object({
  address1: trimmed,
  address2: trimmed,
  city: trimmed,
  province: trimmed,
  postal: trimmed,
})

export type AddressFormValues = z.infer<typeof addressSchema>

export const ADDRESS_DEFAULTS: AddressFormValues = {
  address1: '',
  address2: '',
  city: '',
  province: '',
  postal: '',
}

// Reusable address validation, callable from any form's superRefine.
export const refineAddress = (
  values: AddressFormValues,
  ctx: z.RefinementCtx,
  t: TFunction
): void => {
  if (values.address1.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['address1'],
      message: t('personalInfo.error.addressRequired'),
    })
  }

  if (values.city.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['city'],
      message: t('personalInfo.error.cityRequired'),
    })
  }

  if (!isValidProvinceCode(values.province.toLowerCase())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['province'],
      message: t('personalInfo.error.provinceFormat'),
    })
  }

  if (!testCanadianPostalCode(values.postal.replace(/\s/g, '').toLowerCase())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['postal'],
      message: t('personalInfo.error.postalCodeFormat'),
    })
  }
}
